/**
 * OneAiButton: ONE AI 사이드바 열기/닫기 버튼.
 *
 * 클릭 시:
 *   - isAbleOneAI === true && window.openWehagoAiBot/closeWehagoAiBot 존재: 써드파티헤더 토글
 *   - 그 외 모든 경우: OneAI 프로모 다이얼로그 표시
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { uiActions } from '@/store/slices/uiSlice';
import OneAiPromoDialog from './OneAiPromoDialog';

// 써드파티헤더가 주입하는 전역 함수 타입 선언
declare global {
  interface Window {
    openWehagoAiBot?: () => void;
    closeWehagoAiBot?: () => void;
    openWehagoAiBotPopup?: (params?: Record<string, unknown>) => void;
    closeWehagoAiBotPopup?: () => void;
    isOneAIDeploy?: () => boolean;
    changeConferenceOneAI?: (nowState?: string) => void;
    changeOpenSideOneAI?: (nowState?: boolean) => void;
    useOneAI?: (
      open?: boolean,
      isNewChat?: boolean,
      question?: string,
      options?: { rtcRoomId?: string },
    ) => void;
  }
}

const MAX_CHECK_TIME = 10000;
const CHECK_INTERVAL = 300;
const MAX_CHECK_COUNT = Math.ceil(MAX_CHECK_TIME / CHECK_INTERVAL);

const OneAiButton: React.FC = () => {
  const dispatch = useAppDispatch();
  const isAbleOneAI = useAppSelector((s) => s.translation.isAbleOneAI);
  const openSideBar = useAppSelector((s) => s.ui.openSideBar);

  const [isThirdPartyReady, setIsThirdPartyReady] = useState(false);
  const [isFirstClick, setIsFirstClick] = useState(false);
  const [isPromoDialogOpen, setIsPromoDialogOpen] = useState(false);

  const openSideAIRef = useRef(false);
  const checkCountRef = useRef(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── 사이드바 열림 시 ONE AI 닫기 ──────────────────────────────────────────
  useEffect(() => {
    if (openSideBar && typeof window.closeWehagoAiBot === 'function') {
      window.closeWehagoAiBot();
      openSideAIRef.current = false;
    }
  }, [openSideBar]);

  // ── 써드파티헤더 함수 존재 여부 폴링 (버튼 표시와 무관, 클릭 동작 판단용) ──
  useEffect(() => {
    if (!isAbleOneAI) return;

    const check = () => {
      if (checkCountRef.current >= MAX_CHECK_COUNT) {
        clearInterval(intervalRef.current!);
        intervalRef.current = null;
        return;
      }
      checkCountRef.current += 1;
      if (
        typeof window.openWehagoAiBot === 'function' &&
        typeof window.closeWehagoAiBot === 'function'
      ) {
        setIsThirdPartyReady(true);
        clearInterval(intervalRef.current!);
        intervalRef.current = null;
      }
    };

    checkCountRef.current = 0;
    check();
    intervalRef.current = setInterval(check, CHECK_INTERVAL);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isAbleOneAI]);

  // ── 써드파티헤더에서 X 버튼 또는 모드 전환 시 콜백 등록 ────────────────────
  useEffect(() => {
    window.changeOpenSideOneAI = (nowState = false) => {
      if (!nowState && openSideAIRef.current) {
        openSideAIRef.current = false;
      }
    };

    window.changeConferenceOneAI = (nowState = 'R') => {
      if (nowState === 'R') {
        openSideAIRef.current = true;
        dispatch(uiActions.setOpenSideBar(false));
      }
    };

    return () => {
      window.changeOpenSideOneAI = undefined;
      window.changeConferenceOneAI = undefined;
    };
  }, []);

  // ── 클릭 핸들러 ───────────────────────────────────────────────────────────
  const handleClick = useCallback(() => {
    // 기존 동작 조건: isAbleOneAI true + 써드파티헤더 함수 로드 완료
    if (isAbleOneAI && isThirdPartyReady) {
      try {
        if (window.isOneAIDeploy && !window.isOneAIDeploy()) return;

        const empNo =
          (document.getElementById('h_selected_employee_no') as HTMLInputElement | null)?.value ?? '';
        const oneAI = localStorage.getItem(`${empNo}-one-ai-window-setting`);

        if (!isFirstClick) {
          window.openWehagoAiBot!();
          dispatch(uiActions.setOpenSideBar(false));
          setIsFirstClick(true);
          openSideAIRef.current = true;
          return;
        }

        try {
          const oneAIData = oneAI ? JSON.parse(oneAI) : null;

          if (oneAIData?.type === 'P') { window.closeWehagoAiBot!(); return; }
          if (oneAIData?.type === 'F') { window.openWehagoAiBot!(); dispatch(uiActions.setOpenSideBar(false)); openSideAIRef.current = true; return; }
          if (oneAIData?.type === 'R') { window.closeWehagoAiBot!(); openSideAIRef.current = false; return; }

          window.openWehagoAiBot!();
          dispatch(uiActions.setOpenSideBar(false));
          openSideAIRef.current = true;
        } catch {
          window.openWehagoAiBot!();
          dispatch(uiActions.setOpenSideBar(false));
          openSideAIRef.current = true;
        }
      } catch (error) {
        console.log(error);
      }
      return;
    }

    // 나머지 모든 경우: 프로모 다이얼로그
    setIsPromoDialogOpen(true);
  }, [isAbleOneAI, isThirdPartyReady, isFirstClick]);

  return (
    <>
      <div className="newHeader_btnItem">
        <button type="button" className="newHeader_btn" onClick={handleClick}>
          <span className="newHeader_btnIcon icoRtcAi" />
          <p className="newHeader_btnText">ONE AI</p>
        </button>
      </div>
      {isPromoDialogOpen && (
        <OneAiPromoDialog onClose={() => setIsPromoDialogOpen(false)} />
      )}
    </>
  );
};

export default OneAiButton;
